import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { browse, getStreamers } from '../services/api';
import { trackEvent } from '../services/analytics';
import ContentRail from './ContentRail';
import AdSlot from './AdSlot';

/**
 * Editorial browse section (pre-search, movies/series) — sits below the hero on
 * the homepage for immediate engagement. A quiet streamer chip row tops stacked
 * rails (Top 10, New, genres). Selecting a streamer re-fetches every rail.
 *
 * Warm near-black film-magazine theme (matches HomePage / TheBrief).
 *
 * Props: onItemClick (opens modal), onPlayTrailer
 */
const SERIF = "'Newsreader', Georgia, serif";

const RAILS = [
  { rail: 'top', title: 'Top 10 Films this week', rank: true, type: 'movie', limit: 10 },
  { rail: 'top', title: 'Top 10 Series this week', rank: true, type: 'series', limit: 10 },
  { rail: 'new', title: 'New & Recent', limit: 20 },
  { rail: 'free', title: 'Free to Watch', limit: 20 },
  { rail: 'action', title: 'Action', limit: 20 },
  { rail: 'comedy', title: 'Comedy', limit: 20 },
  { rail: 'drama', title: 'Drama', limit: 20 },
  { rail: 'horror', title: 'Horror', limit: 20 },
  { rail: 'thriller', title: 'Thrillers', limit: 20 },
  { rail: 'romance', title: 'Romance', limit: 20 },
  { rail: 'animation', title: 'Animation', limit: 20 },
  { rail: 'documentary', title: 'Documentaries', limit: 20 },
  { rail: 'family', title: 'Family', limit: 20 },
  { rail: 'crime', title: 'Crime', limit: 20 },
];

// How many rails are above the fold and fetched immediately; the rest stagger in.
const EAGER_RAILS = 4;

// Single-rail skeleton (shown until that rail's data arrives).
const RailSkeleton = () => (
  <div className="mb-2">
    <div className="h-5 w-48 rounded mb-4 animate-poster-pulse" style={{ background: 'rgba(241,236,226,0.07)' }} />
    <div className="flex gap-5 overflow-hidden">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="w-36 sm:w-40 flex-shrink-0">
          <div className="aspect-[2/3] rounded animate-poster-pulse" style={{ background: 'rgba(241,236,226,0.07)' }} />
        </div>
      ))}
    </div>
  </div>
);

const ExploreHome = ({ onItemClick, onPlayTrailer }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeProvider = searchParams.get('provider') || null;

  const [brands, setBrands] = useState([]);
  const [railData, setRailData] = useState({}); // index -> items[] (undefined = still loading)

  useEffect(() => {
    getStreamers().then((d) => setBrands(d.brands || []));
  }, []);

  // Fire every rail but render each the moment IT resolves — don't gate the whole
  // section on the slowest request. The unfiltered Top-10 scroll is the heavy one
  // (~7s cold over the full catalog); previously Promise.all made the fast genre
  // rails (~0.7s) wait for it, so the page sat blank for the worst-case time.
  // Eager rails (above the fold) go out immediately; the genre rails below fire on
  // a short stagger so the first paint isn't 14 concurrent requests fighting 2
  // backend workers.
  useEffect(() => {
    let cancelled = false;
    const timers = [];
    setRailData({});
    const fetchRail = (cfg, i) =>
      browse(cfg.rail, { provider: activeProvider || undefined, type: cfg.type, limit: cfg.limit })
        .then((items) => { if (!cancelled) setRailData((prev) => ({ ...prev, [i]: items })); })
        .catch(() => { if (!cancelled) setRailData((prev) => ({ ...prev, [i]: [] })); });

    RAILS.forEach((cfg, i) => {
      if (i < EAGER_RAILS) {
        fetchRail(cfg, i);                     // above the fold — fetch now
      } else {
        // below the fold — stagger so we don't open 14 sockets at once
        timers.push(setTimeout(() => fetchRail(cfg, i), 250 + (i - EAGER_RAILS) * 120));
      }
    });
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [activeProvider]);

  const handleProviderChange = useCallback((key) => {
    trackEvent('Streamer Filter Changed', { provider: key || 'all' });
    const next = new URLSearchParams(searchParams);
    if (key) next.set('provider', key); else next.delete('provider');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const allLoaded = Object.keys(railData).length >= RAILS.length;
  const anyResults = Object.values(railData).some((l) => l && l.length);

  const pill = (selected) =>
    `px-3 py-[5px] rounded-full text-[12.5px] border whitespace-nowrap transition-all ${
      selected
        ? 'bg-binge-ink text-binge-bg border-binge-ink font-semibold'
        : 'bg-transparent text-binge-sub border-[rgba(241,236,226,0.2)] hover:border-[rgba(241,236,226,0.45)] hover:text-binge-ink'
    }`;

  return (
    <div className="animate-fadein">
      {/* Heading + streamer chips */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8 pb-4" style={{ borderBottom: '1px solid rgba(241,236,226,0.08)' }}>
        <div>
          <span style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6E675D' }}>Or just browse</span>
          <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 30, letterSpacing: '-0.01em', color: '#F1ECE2', marginTop: 5 }}>
            What’s good right now
          </h2>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-0.5 max-w-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button className={pill(!activeProvider)} onClick={() => handleProviderChange(null)}>All</button>
          {brands.map((b) => (
            <button key={b.key} className={pill(activeProvider === b.key)} onClick={() => handleProviderChange(b.key)}>{b.label}</button>
          ))}
        </div>
      </div>

      {allLoaded && !anyResults && (
        <div className="text-center py-16" style={{ color: '#8F887C' }}>
          <p className="font-serif italic text-lg">No titles for this filter yet — try another streamer, or just search above.</p>
        </div>
      )}

      {RAILS.map((cfg, i) => {
        const items = railData[i];
        // Still loading this rail → its own skeleton (so fast rails paint while the
        // heavy Top-10 scroll is still resolving). Loaded-but-empty → hide it.
        let block;
        if (items === undefined) {
          block = <RailSkeleton key={`sk-${i}`} />;
        } else if (items.length === 0) {
          block = null;
        } else {
          block = (
            <ContentRail
              key={`${cfg.title}-${i}`}
              title={cfg.title}
              items={items}
              rank={cfg.rank}
              onItemClick={onItemClick}
              onPlayTrailer={onPlayTrailer}
            />
          );
        }
        if (i === 1) {
          return (
            <React.Fragment key={`frag-${i}`}>
              {block}
              <AdSlot id="explore-mid" format="leaderboard" />
            </React.Fragment>
          );
        }
        return block;
      })}
    </div>
  );
};

export default ExploreHome;
